import React, { Fragment, useState, useCallback, Children } from "react";
import { List, Datagrid, Edit, Create, ImageField, FileInput, FormDataConsumer, ImageInput, ReferenceInput, SelectInput, ArrayInput, SimpleFormIterator, TextField, EditButton, TextInput, BooleanField, NumberInput, TabbedForm, FormTab, TranslatableFields, BooleanInput, CreateButton, TopToolbar, ReferenceArrayInput, SelectArrayInput, ReferenceArrayField, SingleFieldList, ChipField, useInput, FileField, translate, useTranslate, Filter } from 'react-admin';
import { Card, Typography, CardContent, TextField as MUITextField } from '@material-ui/core';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import OrderingField from '../Fields/Ordering.field';
import { required } from 'react-admin';
import { translations } from '../../i18n/i18n';
import ConfigGeneral from '../../config/general';
import RichTextInput from 'ra-input-rich-text';
import { uploadFile } from '../Providers/Data.provider';
import { useForm } from 'react-final-form';
import FileManagerGroupsInputProvider from "../Fields/FileManager/FileManagerGroupsInputProvider.component";
import FileManagerContext from "../Fields/FileManager/FileManagerContext.component";
import FilesInput from "../Fields/FileManager/FilesInput.component";
import { makeStyles, withStyles } from '@material-ui/styles';

const Filters = withStyles({ select: { width: 206 } })(
	({ classes, ...props }) => (
	<Filter {...props}>
		<TextInput label="resources.places.fields.name_display" source="translations.name" alwaysOn />
		<ReferenceArrayInput source="placeTypes" size="small" label="resources.places.fields.place_type_ids" reference="placetypes" fullWidth alwaysOn>
			<SelectArrayInput optionText="name_display" options={{ fullWidth: true }} className={classes.select} />
		</ReferenceArrayInput>
		<SelectInput source="state" allowEmpty={false} label="filters.state.label" choices={[
			{ id: '', name: translations.filters.state.all },
			{ id: '1', name: translations.filters.state.active },
			{ id: '0', name: translations.filters.state.inactive },
		]} alwaysOn />
	</Filter>
));

const ListActions = ({ basePath }) => (
	<TopToolbar>
		<CreateButton basePath={basePath} />
	</TopToolbar>
);

const useHorizontalIteratorStyle = makeStyles(() => ({
	form: {
		alignItems: 'flex-end',
		display: 'flex',
		flexDirection: 'row',
		textAlign: 'left',

		'& > div': {
			margin: '0 20px',
		},
	},
}));
const useCustomStyle = makeStyles(() => ({
	formTabHorizontal: {
		display: 'flex',
		textAlign: 'left',

		'&& > *': {
			flexGrow: 1,
			padding: '2px 10px',
			maxWidth: '50%',
		},
		'&& > * > *': {
			width: '100%',
		},
	},
	modelFileInput: {
		'&& label': {
			display: 'none',
		},
		'&& label[for="models[0].preview"], && label[for="models[0].model"]': {
			display: 'block',
			marginTop: -30,
			position: 'absolute',
			textAlign: 'left',
		},
		'&& > div': {
			paddingTop: 0,
		},
		'&& > div > *': {
			fontSize: 12,
			lineHeight: '32px',
		},
		'&& .previews button': {
			display: 'none',
		},
		'&& .previews': {
			marginTop: -36,
			textAlign: 'center',
		},
	},
	previewFileField: {
		'&& img': {
			height: 70,
			objectFit: 'contain',
			width: 316,
		},
	},
	modelFileField: {
		alignItems: 'center',
		display: 'flex',
		height: 70,
		justifyContent: 'center',
		width: 316,
	},
}));

const PlaceTypeIcon = LocationOnIcon;
const PlaceTypeList = (props) => (
	<List actions={<ListActions />} filters={<Filters />} perPage={25} sort={{ field: 'ordering', order: 'ASC' }} {...props}>
		<Datagrid>
			<TextField source="id" />
			<ReferenceArrayField reference="placetypes" source="place_type_ids">
                <SingleFieldList>
                    <ChipField source="name_display" />
                </SingleFieldList>
            </ReferenceArrayField>
			<TextField source="name_display" sortable={false} />
			<TextField source="location_display" sortable={false} />
			<BooleanField source="state" looseValue />
			<OrderingField source="ordering" />
			<EditButton basePath="/places" />
		</Datagrid>
	</List>
);

const PlaceTypeEdit = (props) => {
	const iteratorClasses = useHorizontalIteratorStyle();
	const classes = useCustomStyle();
	const translate = useTranslate();

	return (
		<Edit
			title=" "
			transform={(data) => {
				delete data.place_types;
				return data;
			}}
			undoable={false}
			{...props}
		>
			<TabbedForm syncWithLocation={false}>
				<FormTab label={translate('resources.places.tab.summary')} contentClassName={classes.formTabHorizontal}>
					<div>
						<ReferenceArrayInput source="place_type_ids" label={translate('resources.places.fields.place_type_ids')} reference="placetypes" sort={{ field: 'ordering', order: 'ASC' }} fullWidth>
							<SelectArrayInput optionText="name_display" fullWidth validate={[required()]} />
						</ReferenceArrayInput>
						<NumberInput source="coordinate_x" label={translate('resources.places.fields.coordinate_x')} fullWidth validate={[required()]} />
						<NumberInput source="coordinate_y" label={translate('resources.places.fields.coordinate_y')} fullWidth validate={[required()]} />
						<BooleanInput source="state" label={translate('resources.places.fields.state')} />
					</div>
					<div>
						<TranslatableFields locales={ConfigGeneral.locales.map(locale => locale.code)}>
							<TextInput source="name" fullWidth />
							<TextInput source="alt_name" fullWidth />
							<TextInput source="location" fullWidth />
							<TextInput source="guide_intro" fullWidth />
							<RichTextInput source="guide" fullWidth />
						</TranslatableFields>
					</div>
				</FormTab>
				<FormTab label={translate('resources.places.tab.photos')}>
					<FileManagerContext inputSource="photos" filesGroupIdKey="photo_type_id" inputSourcePreviewKey="thumb_url">
						<FileManagerGroupsInputProvider
							groupsResourceName="phototypes"
							groupsResourceTitleKey="name_display"
							groupsResourceSort={{ field: 'ordering', order: 'ASC' }}
							filesGroupIdKey="photo_type_id"
						/>
					</FileManagerContext>
				</FormTab>
				<FormTab label={translate('resources.places.tab.videos')}>
					<ArrayInput source="videos">
						<SimpleFormIterator classes={iteratorClasses}>
							<SelectInput source="source" choices={[
								{ id: 'youtube', name: 'YouTube' },
								{ id: 'vimeo', name: 'Vimeo' },
							]} validate={[required()]} />
							<TextInput source="video_id" validate={[required()]} />
							<BooleanInput source="reconstruction" />
							<TextInput source="caption" />
						</SimpleFormIterator>
					</ArrayInput>
				</FormTab>
				{/*<FormTab label={translate('resources.places.tab.models')}>
					<ArrayInput source="models">
						<SimpleFormIterator classes={iteratorClasses}>
							<ReferenceInput source="model_type_id" reference="modeltypes" sort={{ field: 'ordering', order: 'ASC' }}>
								<SelectInput optionText="name_display" validate={[required()]} />
							</ReferenceInput>
							<FormDataConsumer>
								{({ formData, scopedFormData, getSource, ...rest }) => {
									if(scopedFormData && scopedFormData.preview_url) {
										//File already uploaded
										return <ImageField source={getSource("preview_url")} label={translate('resources.places.fields.preview')} className={classes.previewFileField} />;
									} else {
										//File input
										return <ImageInput source={getSource("preview")} label={translate('resources.places.fields.preview')} accept="image/*" validate={[required()]} placeholder={translate('resources.places.fields.previewInput')} classes={{root: classes.modelFileInput}}>
											<FileField source="src" title="title" download />
										</ImageInput>;
									}
								}}
							</FormDataConsumer>
							<FormDataConsumer>
								{({ formData, scopedFormData, getSource, ...rest }) => {
									if(scopedFormData && scopedFormData.preview_url) {
										//File already uploaded
										return <FileField source={getSource("model_url")} label={translate('resources.places.fields.model')} title={getSource("filename")} target="_blank" className={classes.modelFileField} />;
									} else {
										//File input
										return <FileInput source={getSource("model")} label={translate('resources.places.fields.model')} accept=".gltf,.glb" validate={[required()]} placeholder={translate('resources.places.fields.modelInput')} classes={{root: classes.modelFileInput}}>
											<FileField source="src" title="title" download />
										</FileInput>;
									}
								}}
							</FormDataConsumer>
							<TextInput source="caption" />
						</SimpleFormIterator>
					</ArrayInput>
				</FormTab>*/}
			</TabbedForm>
		</Edit>
	)
};

const PlaceTypeCreate = (props) => {
	const iteratorClasses = useHorizontalIteratorStyle();
	const classes = useCustomStyle();
	const translate = useTranslate();

    return <Create
		title=" "
		transform={(data) => {
			delete data.place_types;
			return data;
		}}
		undoable={false}
		{...props}
	>
        <TabbedForm syncWithLocation={false}>
			<FormTab label={translate('resources.places.tab.summary')} contentClassName={classes.formTabHorizontal}>
				<div>
					<ReferenceArrayInput source="place_type_ids" label={translate('resources.places.fields.place_type_ids')} reference="placetypes" sort={{ field: 'ordering', order: 'ASC' }}>
						<SelectArrayInput optionText="name_display" fullWidth validate={[required()]} />
					</ReferenceArrayInput>
					<NumberInput source="coordinate_x" label={translate('resources.places.fields.coordinate_x')} fullWidth validate={[required()]} />
					<NumberInput source="coordinate_y" label={translate('resources.places.fields.coordinate_y')} fullWidth validate={[required()]} />
					<BooleanInput source="state" label={translate('resources.places.fields.state')} />
				</div>
				<div>
					<TranslatableFields locales={ConfigGeneral.locales.map(locale => locale.code)}>
						<TextInput source="name" fullWidth />
						<TextInput source="alt_name" fullWidth />
						<TextInput source="location" fullWidth />
						<TextInput source="guide_intro" fullWidth />
						<RichTextInput source="guide" fullWidth />
					</TranslatableFields>
				</div>
			</FormTab>
			<FormTab label={translate('resources.places.tab.photos')}>
				<FileManagerContext inputSource="photos" inputSourcePreviewKey="thumb_url" filesGroupIdKey="photo_type_id">
					<FileManagerGroupsInputProvider
						groupsResourceName="phototypes"
						groupsResourceTitleKey="name_display"
						groupsResourceSort={{ field: 'ordering', order: 'ASC' }}
						filesGroupIdKey="photo_type_id"
					/>
				</FileManagerContext>
			</FormTab>
			<FormTab label={translate('resources.places.tab.videos')}>
				<ArrayInput source="videos">
					<SimpleFormIterator classes={iteratorClasses}>
						<SelectInput source="source" choices={[
							{ id: 'youtube', name: 'YouTube' },
							{ id: 'vimeo', name: 'Vimeo' },
						]} validate={[required()]} />
						<TextInput source="video_id" validate={[required()]} />
						<BooleanInput source="reconstruction" />
						<TextInput source="caption" />
					</SimpleFormIterator>
				</ArrayInput>
			</FormTab>
			{/*<FormTab label={translate('resources.places.tab.models')}>
				<ArrayInput source="models">
					<SimpleFormIterator classes={iteratorClasses}>
						<ReferenceInput source="model_type_id" reference="modeltypes" sort={{ field: 'ordering', order: 'ASC' }}>
							<SelectInput optionText="name_display" validate={[required()]} />
						</ReferenceInput>
						<ImageInput source="preview" label={translate('resources.places.fields.preview')} accept="image/*" validate={[required()]} placeholder={translate('resources.places.fields.previewInput')} classes={{root: classes.modelFileInput}}>
							<FileField source="src" title="title" download />
						</ImageInput>
						<FileInput source="model" label={translate('resources.places.fields.model')} accept=".gltf,.glb" validate={[required()]} placeholder={translate('resources.places.fields.modelInput')} classes={{root: classes.modelFileInput}}>
							<FileField source="src" title="title" download />
						</FileInput>
						<TextInput source="caption" />
					</SimpleFormIterator>
				</ArrayInput>
			</FormTab>*/}
        </TabbedForm>
    </Create>
};

let exports = {
	list: PlaceTypeList,
	edit: PlaceTypeEdit,
	create: PlaceTypeCreate,
	icon: PlaceTypeIcon,
};
export default exports;